import { Box } from '#/components/Box/Box';
import { Divider } from '#/components/Divider/Divider';
import { Hide } from '#/components/Hide/Hide';
import { Icon } from '#/components/Icon/Icon';
import { Text } from '#/components/Text/Text';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import type { SearchContextHeaderData } from '#/modules/search/containers/SearchContextHeader/SearchContextHeaderData';
import type { SearchContextHeaderLocationState } from '#/modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { searchContextHeaderGetUrl } from '#/modules/search/routing/helpers/searchContextHeaderGetUrl';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import { Link, ResultsCount } from './SearchContextHeader.styled';
import { SearchContextHeaderLinkText } from './SearchContextHeaderLinkText';

type Props = {
  noDivider?: boolean;
  searchContext: SearchContextHeaderData;
};

export function SearchContextHeader({ noDivider, searchContext }: Props) {
  const { i18n, locale } = useI18n();

  const {
    searchResultsUrl,
    searchResultsState,
    previousResult,
    previousResultPageIndex,
    nextResult,
    nextResultPageIndex,
    resultIndex,
    totalResults,
    setPageIndex,
  } = searchContext;

  const { embedSearchWidget } = useEmbedSearchWidget();

  const nextUrl = nextResult
    ? searchContextHeaderGetUrl({
        result: nextResult,
        embedSearchWidgetId: embedSearchWidget?.id ?? null,
        locale,
      })
    : '';

  const previousUrl = previousResult
    ? searchContextHeaderGetUrl({
        result: previousResult,
        embedSearchWidgetId: embedSearchWidget?.id ?? null,
        locale,
      })
    : '';

  return (
    <Text
      qaId="search-context-header"
      fontSize="14px"
      color="lightContentGrey"
      letterSpacing=".26px"
    >
      <Box display="flex" justifyContent="space-between">
        <Link
          to={searchResultsUrl}
          state={searchResultsState}
          onClick={() =>
            trackClicked('Back To Results', {
              header_position: 'top',
            })
          }
          qaId="search-context-back-to-search"
        >
          <Icon name="list" size={16} colorName="lightContentGrey" />{' '}
          <Box verticalAlign="middle" display="inline-block">
            {i18n('Back to results')}
          </Box>
        </Link>
        <Box display="flex" alignItems="center">
          {previousResult ? (
            <Link
              qaId="search-context-previous-result"
              to={previousUrl}
              state={
                {
                  showSearchContext: true,
                } satisfies SearchContextHeaderLocationState
              }
              onClick={() => {
                trackClicked('Previous Listing', {
                  org_id:
                    'orgID' in previousResult
                      ? previousResult.orgID
                      : undefined,
                  org_type:
                    'orgType' in previousResult
                      ? previousResult.orgType
                      : undefined,
                  result_id: previousResult.objectID,
                  header_position: 'top',
                });

                if (previousResultPageIndex !== null) {
                  setPageIndex(previousResultPageIndex, false);
                }
              }}
            >
              <SearchContextHeaderLinkText text="previous" />
            </Link>
          ) : (
            <SearchContextHeaderLinkText text="previous" />
          )}

          <Hide xs sm>
            <ResultsCount>
              {resultIndex + 1} of {totalResults}
            </ResultsCount>
          </Hide>

          <Hide md lg xl>
            <Box mx="8px">|</Box>
          </Hide>

          {nextResult ? (
            <Link
              qaId="search-context-next-result"
              to={nextUrl}
              state={
                {
                  showSearchContext: true,
                } satisfies SearchContextHeaderLocationState
              }
              onClick={() => {
                trackClicked('Next Listing', {
                  org_id: 'orgID' in nextResult ? nextResult.orgID : undefined,
                  org_type:
                    'orgUrl' in nextResult ? nextResult.orgType : undefined,
                  result_id: nextResult.objectID,
                  header_position: 'top',
                });

                if (nextResultPageIndex !== null) {
                  setPageIndex(nextResultPageIndex, false);
                }
              }}
            >
              <SearchContextHeaderLinkText text="next" />
            </Link>
          ) : (
            <SearchContextHeaderLinkText text="next" />
          )}
        </Box>
      </Box>
      {!noDivider && <Divider marginTop={16} marginBottom={16} />}
    </Text>
  );
}
